import {
  ServiceTopic,
  ServiceTopicData,
  serviceTopicCollectionKey,
  schoolAIServiceTopicCollectionKey,
  convertToServiceTopic
} from "@/entities/service_topic";
import { schoolCollectionKey } from "@/entities/school";
import firebase from "firebase/app";
import "firebase/firestore";
import store from "@/store";

export async function getServiceTopics({
  toSchoolAIUser
}: {
  toSchoolAIUser?: boolean;
}): Promise<ServiceTopic[]> {
  const db = firebase.firestore();

  const snapshot = await db
    .collection(
      toSchoolAIUser
        ? schoolAIServiceTopicCollectionKey
        : serviceTopicCollectionKey
    )
    .get();
  if (snapshot.empty) {
    return [];
  }

  return snapshot.docs
    .map(doc => convertToServiceTopic(doc.data(), doc.ref))
    .sort((a, b) => b.data.timestamp - a.data.timestamp);
}

export async function getServiceTopicsOfSchool(): Promise<ServiceTopic[]> {
  const schoolRef = store.state.school?.ref;
  const db = schoolRef;
  if (!db) {
    return [];
  }

  const snapshot = await db.collection(serviceTopicCollectionKey).get();
  if (snapshot.empty) {
    return [];
  }

  return snapshot.docs
    .map(doc => convertToServiceTopic(doc.data(), doc.ref))
    .sort((a, b) => b.data.timestamp - a.data.timestamp);
}

export async function getServiceTopicsOfParentSchools(
  parentSchoolDocIds: string[]
): Promise<ServiceTopic[]> {
  if (parentSchoolDocIds.length === 0) {
    return [];
  }

  const db = firebase.firestore();
  const snapshots = await Promise.all(
    parentSchoolDocIds.map(id =>
      db
        .collection(schoolCollectionKey)
        .doc(id)
        .collection(serviceTopicCollectionKey)
        .get()
    )
  );

  const topics: ServiceTopic[] = [];
  snapshots.forEach(snapshot => {
    snapshot.docs.forEach(doc => {
      topics.push(convertToServiceTopic(doc.data(), doc.ref));
    });
  });

  return topics
    .filter(topic => topic.data.deliverToChildSchools !== false)
    .sort((a, b) => b.data.timestamp - a.data.timestamp);
}

export async function createServiceTopic({
  url,
  label,
  timestamp,
  toSchoolAIUser
}: {
  url: string;
  label: string;
  timestamp: number;
  toSchoolAIUser?: boolean;
}): Promise<{ id: string; data: ServiceTopicData }> {
  const roleType = store.state.role?.data.type;
  if (!roleType) {
    throw new Error("Role not found");
  }

  let db:
    | firebase.firestore.DocumentReference
    | firebase.firestore.Firestore
    | null = null;
  if (roleType === "admin") {
    db = firebase.firestore();
  } else {
    if (toSchoolAIUser) {
      throw new Error("Tutor cannot create service topic for school AI user");
    }
    db = roleType === "tutor" ? store.state.school?.ref ?? null : null;
  }

  if (!db) {
    throw new Error("No access to database");
  }

  const ref = await db
    .collection(
      toSchoolAIUser
        ? schoolAIServiceTopicCollectionKey
        : serviceTopicCollectionKey
    )
    .add({
      url,
      label,
      timestamp
    });
  return {
    id: ref!.id,
    data: {
      url,
      label,
      timestamp
    } as ServiceTopicData
  };
}

export async function updateServiceTopic(
  ref: firebase.firestore.DocumentReference,
  {
    url,
    label,
    timestamp
  }: {
    url?: string;
    label?: string;
    timestamp?: number;
  }
): Promise<{ id: string; data: ServiceTopicData } | null> {
  const snapshot = await ref.get();
  const data = snapshot.data();
  if (!snapshot.exists || !data) {
    console.error("Failed to get service topic data");
    return null;
  }
  const next = { ...data } as ServiceTopicData;
  if (url) next.url = url;
  if (label) next.label = label;
  if (timestamp) next.timestamp = timestamp;
  await ref.update(next);
  return {
    id: ref.id,
    data: next
  };
}

export async function deleteServiceTopic(
  ref: firebase.firestore.DocumentReference
): Promise<void> {
  await ref.delete();
}
